// src/utils/wordpressHelper.js
// Helper to fetch and parse acts from WordPress API

const WP_API_BASE = 'https://cms.scotbase.net/wp-json/wp/v2';

const createSlugFromText = (text) => {
  if (!text || typeof text !== 'string') return null;

  return text
    .toLowerCase()
    .trim()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Decode HTML entities (e.g., &amp; to &)
 */
const decodeHTMLEntities = (text) => {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const normalizeTerm = (term) => {
  if (!term?.slug) return null;
  return {
    name: decodeHTMLEntities(term.name),
    slug: term.slug,
  };
};

const getEmbeddedTerms = (wpAct, taxonomy) => {
  const termGroups = wpAct._embedded?.['wp:term'] || [];
  return termGroups
    .flat()
    .filter((term) => term?.taxonomy === taxonomy)
    .map(normalizeTerm)
    .filter(Boolean);
};

const getAcfTerms = (value) => {
  if (!value) return [];

  const items = Array.isArray(value) ? value : [value];

  return items
    .map((item) => {
      if (typeof item === 'object' && item.slug) {
        return normalizeTerm(item);
      }
      return null;
    })
    .filter(Boolean);
};

const getNumericIds = (value) => {
  if (!value) return [];
  const items = Array.isArray(value) ? value : [value];
  return items.filter((item) => typeof item === 'number');
};

const resolveActTerms = async (wpAct, taxonomy, idField, acfField, fetchFn) => {
  const embeddedTerms = getEmbeddedTerms(wpAct, taxonomy);
  if (embeddedTerms.length > 0) {
    return embeddedTerms;
  }

  const acfTerms = getAcfTerms(wpAct.acf?.[acfField]);
  if (acfTerms.length > 0) {
    return acfTerms;
  }

  const ids = getNumericIds(wpAct[idField]);
  if (ids.length === 0) {
    return [];
  }

  const fetchedTerms = await Promise.all(ids.map((id) => fetchFn(id)));
  return fetchedTerms.filter(Boolean);
};

const categoryCache = {};
const genreCache = {};
const mediaCache = {};
const mediaFetchPromises = {};
const categoryFetchPromises = {};
const genreFetchPromises = {};
let taxonomiesPreloaded = false;
let taxonomiesPreloadPromise = null;

const preloadTaxonomyCaches = async () => {
  if (taxonomiesPreloaded) {
    return;
  }

  if (taxonomiesPreloadPromise) {
    return taxonomiesPreloadPromise;
  }

  taxonomiesPreloadPromise = (async () => {
    try {
      const [categoriesResponse, genresResponse] = await Promise.all([
        fetch(`${WP_API_BASE}/act_category?per_page=100`),
        fetch(`${WP_API_BASE}/act_genre?per_page=100`),
      ]);

      if (categoriesResponse.ok) {
        const categories = await categoriesResponse.json();
        categories.forEach((category) => {
          categoryCache[category.id] = {
            name: decodeHTMLEntities(category.name),
            slug: category.slug,
          };
        });
      }

      if (genresResponse.ok) {
        const genres = await genresResponse.json();
        genres.forEach((genre) => {
          genreCache[genre.id] = {
            name: decodeHTMLEntities(genre.name),
            slug: genre.slug,
          };
        });
      }

      taxonomiesPreloaded = true;
    } catch (error) {
      console.warn('Failed to preload taxonomy caches:', error);
    } finally {
      taxonomiesPreloadPromise = null;
    }
  })();

  return taxonomiesPreloadPromise;
};

const fetchCachedTerm = async (endpoint, cache, inFlightPromises, termId) => {
  if (cache[termId]) {
    return cache[termId];
  }

  if (inFlightPromises[termId]) {
    return inFlightPromises[termId];
  }

  inFlightPromises[termId] = (async () => {
    try {
      const response = await fetch(`${WP_API_BASE}/${endpoint}/${termId}`);

      if (!response.ok) {
        return null;
      }

      const term = await response.json();
      cache[termId] = {
        name: decodeHTMLEntities(term.name),
        slug: term.slug,
      };
      return cache[termId];
    } catch {
      return null;
    } finally {
      delete inFlightPromises[termId];
    }
  })();

  return inFlightPromises[termId];
};

export const fetchCategoryName = async (categoryId) => {
  await preloadTaxonomyCaches();
  return fetchCachedTerm('act_category', categoryCache, categoryFetchPromises, categoryId);
};

export const fetchGenreName = async (genreId) => {
  await preloadTaxonomyCaches();
  return fetchCachedTerm('act_genre', genreCache, genreFetchPromises, genreId);
};

export const fetchMediaUrl = async (mediaId) => {
  if (!mediaId || typeof mediaId !== 'number') {
    return null;
  }

  if (mediaCache[mediaId]) {
    return mediaCache[mediaId];
  }

  if (mediaFetchPromises[mediaId]) {
    return mediaFetchPromises[mediaId];
  }

  mediaFetchPromises[mediaId] = (async () => {
    try {
      const response = await fetch(`${WP_API_BASE}/media/${mediaId}`);

      if (!response.ok) {
        return null;
      }

      const media = await response.json();
      const url = media.source_url || media.url || null;
      mediaCache[mediaId] = url;
      return url;
    } catch {
      return null;
    } finally {
      delete mediaFetchPromises[mediaId];
    }
  })();

  return mediaFetchPromises[mediaId];
};

/**
 * Parse WordPress act data to our app format
 */
export const parseWordPressAct = async (wpAct) => {
  try {
    const [validCategories, validGenres] = await Promise.all([
      resolveActTerms(wpAct, 'act_category', 'act_category', 'category', fetchCategoryName),
      resolveActTerms(wpAct, 'act_genre', 'act_genre', 'genre', fetchGenreName),
    ]);

    // Extract image - prioritize ACF fields, then featured_media
    // Use large size for cards - good quality without blur
    let imageUrl = '/images/placeholder.svg';
    
    // Check ACF fields (can be an object with url, URL string, or numeric ID)
    // Priority: act_image > icon_image > detail_image > main_image > image > featured_media
    if (wpAct.acf?.act_image?.sizes?.large) {
      // Use large size (1024px) - high quality for cards
      imageUrl = wpAct.acf.act_image.sizes.large;
    } else if (wpAct.acf?.act_image?.sizes?.medium_large) {
      // Fall back to medium_large (768px)
      imageUrl = wpAct.acf.act_image.sizes.medium_large;
    } else if (wpAct.acf?.act_image?.sizes?.medium_large) {
      // Fall back to medium_large (768px)
      imageUrl = wpAct.acf.act_image.sizes.medium_large;
    } else if (wpAct.acf?.act_image?.url) {
      // Fall back to full size
      imageUrl = wpAct.acf.act_image.url;
    } else if (typeof wpAct.acf?.act_image === 'number') {
      // ACF act_image is a numeric ID
      const fetchedUrl = await fetchMediaUrl(wpAct.acf.act_image);
      if (fetchedUrl) imageUrl = fetchedUrl;
    } else if (typeof wpAct.acf?.act_image === 'string' && wpAct.acf.act_image.startsWith('http')) {
      // ACF act_image is a direct URL string
      imageUrl = wpAct.acf.act_image;
    } else if (wpAct.acf?.icon_image?.sizes?.large) {
      imageUrl = wpAct.acf.icon_image.sizes.large;
    } else if (wpAct.acf?.icon_image?.sizes?.medium_large) {
      imageUrl = wpAct.acf.icon_image.sizes.medium_large;
    } else if (wpAct.acf?.icon_image?.url) {
      // ACF icon_image object with URL
      imageUrl = wpAct.acf.icon_image.url;
    } else if (typeof wpAct.acf?.icon_image === 'number') {
      // ACF icon_image is a numeric ID
      const fetchedUrl = await fetchMediaUrl(wpAct.acf.icon_image);
      if (fetchedUrl) imageUrl = fetchedUrl;
    } else if (typeof wpAct.acf?.icon_image === 'string' && wpAct.acf.icon_image.startsWith('http')) {
      // ACF icon_image is a direct URL string
      imageUrl = wpAct.acf.icon_image;
    } else if (wpAct.acf?.detail_image?.url) {
      // Try detail_image as object
      imageUrl = wpAct.acf.detail_image.url;
    } else if (typeof wpAct.acf?.detail_image === 'number') {
      // Try detail_image as numeric ID
      const fetchedUrl = await fetchMediaUrl(wpAct.acf.detail_image);
      if (fetchedUrl) imageUrl = fetchedUrl;
    } else if (wpAct.acf?.main_image?.url) {
      imageUrl = wpAct.acf.main_image.url;
    } else if (wpAct.acf?.image?.url) {
      imageUrl = wpAct.acf.image.url;
    } else if (wpAct.featured_media && wpAct._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
      // Fall back to WordPress featured media
      imageUrl = wpAct._embedded['wp:featuredmedia'][0].source_url;
    }

    // Extract detail image for artist detail page - use full size for best quality
    let detailImageUrl = imageUrl; // Default to same as card image
    if (wpAct.acf?.act_image?.url) {
      // Use full size URL for detail pages
      detailImageUrl = wpAct.acf.act_image.url;
    } else if (wpAct.acf?.act_image?.sizes?.['2048x2048']) {
      // Or use largest available size
      detailImageUrl = wpAct.acf.act_image.sizes['2048x2048'];
    } else if (wpAct.acf?.act_image?.sizes?.['1536x1536']) {
      detailImageUrl = wpAct.acf.act_image.sizes['1536x1536'];
    } else if (wpAct.acf?.detail_image?.url) {
      detailImageUrl = wpAct.acf.detail_image.url;
    }

    // Map to our app's format
    // Check if this is a dinner speaker or themed night (they might not have genres)
    const isDinnerSpeaker = validCategories.some(c => 
      c.slug?.includes('dinner-speaker') || 
      c.name?.toLowerCase().includes('dinner speaker')
    );
    const isThemedNight = validCategories.some(c => 
      c.slug?.includes('themed-night') || 
      c.name?.toLowerCase().includes('themed night')
    );
    
    // Determine genre - don't default to 'Pop' for dinner speakers or themed nights
    let genreValue = validGenres[0]?.name || null;
    if (!genreValue && isDinnerSpeaker) {
      genreValue = 'Dinner Speaker';
    } else if (!genreValue && isThemedNight) {
      genreValue = 'Themed Night';
    } else if (!genreValue) {
      genreValue = 'Pop'; // Only default to Pop for regular performance acts
    }
    
    const rawName = wpAct.title?.rendered || 'Untitled Act';
    const rawTribute = wpAct.acf?.tribute_to || wpAct.title?.rendered || '';
    const rawDescription =
      wpAct.acf?.description ||
      wpAct.content?.rendered?.replace(/<[^>]*>/g, '') ||
      wpAct.excerpt?.rendered?.replace(/<[^>]*>/g, '') ||
      '';

    const decodedName = decodeHTMLEntities(rawName);

    const generatedSlug = createSlugFromText(decodedName);

    return {
      id: `wp-${wpAct.id}`, // Prefix with 'wp-' to avoid conflicts
      slug: generatedSlug || wpAct.slug || `act-${wpAct.id}`,
      sourceSlug: wpAct.slug || null,
      name: decodedName,
      tribute: decodeHTMLEntities(rawTribute),
      image: imageUrl,
      detailImage: detailImageUrl,
      parentCategory: validCategories[0]?.slug || null,
      act_category: validCategories.map(c => c.slug),
      genre: genreValue,
      act_genre: validGenres.map(g => g.slug),
      location: wpAct.acf?.location || 'Scotland',
      price: wpAct.acf?.price || 'POA',
      description: decodeHTMLEntities(rawDescription),
      featured: wpAct.acf?.featured || false,
      availability: wpAct.acf?.availability || 'Available',
      bookingCount: wpAct.acf?.booking_count || 0,
      rating: wpAct.acf?.rating || null,
      videoUrl: wpAct.acf?.video_url || null,
      tags: wpAct.acf?.tags || [],
      categories: validCategories.map(c => c.name),
      genres: validGenres.map(g => g.name),
      fromWordPress: true // Flag to identify WordPress acts
    };
  } catch (error) {
    console.error('Error parsing WordPress act:', error, wpAct);
    return null;
  }
};

/**
 * Fetch a single page of acts from the WordPress API.
 */
const fetchWordPressActPage = async (page, perPage) => {
  const response = await fetch(
    `${WP_API_BASE}/act?per_page=${perPage}&page=${page}&_embed&acf_format=standard`
  );

  if (!response.ok) {
    throw new Error(`WordPress API error: ${response.status} on page ${page}`);
  }

  const acts = await response.json();

  return Array.isArray(acts) ? acts : [];
};

/**
 * Fetch all raw act records from WordPress, paginating until every page is loaded.
 * Pass a limit to fetch only the first N acts (used by the home page).
 */
const fetchAllWordPressActRecords = async (limit = null) => {
  const pageSize = 100;

  if (limit) {
    const acts = await fetchWordPressActPage(1, limit);
    return acts.slice(0, limit);
  }

  const wpActs = [];
  let page = 1;

  while (true) {
    const acts = await fetchWordPressActPage(page, pageSize);

    if (acts.length === 0) {
      break;
    }

    wpActs.push(...acts);

    if (acts.length < pageSize) {
      break;
    }

    page += 1;
  }

  return wpActs;
};

const parseActsInBatches = async (wpActs, batchSize = 20) => {
  const parsedActs = [];

  for (let i = 0; i < wpActs.length; i += batchSize) {
    const batch = wpActs.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map((wpAct) => parseWordPressAct(wpAct)));
    parsedActs.push(...batchResults.filter(Boolean));
  }

  return parsedActs;
};

/**
 * Fetch acts from WordPress.
 * Without a limit, paginates through every page so all acts are loaded.
 * With a limit, fetches only as many acts as needed for lightweight views.
 */
export const fetchActsFromWordPress = async (limit = null) => {
  try {
    await preloadTaxonomyCaches();
    const wpActs = await fetchAllWordPressActRecords(limit);
    return await parseActsInBatches(wpActs);
  } catch (error) {
    console.error('❌ Error fetching acts from WordPress:', error);
    return [];
  }
};


